import { Elements } from '@/components/elements';
import clsx from 'clsx';
import type { ReactNode } from 'react';

export type StrategicComparisonRow = {
    label: string;
    predictableBenefits: ReactNode;
    adminOnlyModel: string;
};

export type StrategicComparisonProps = {
    title?: string;
    predictableBenefitsHeader?: string;
    adminOnlyModelHeader?: string;
    rows?: StrategicComparisonRow[];
    className?: string;
    tableClassName?: string;
};

const DEFAULT_TITLE = 'Strategic Comparison for Pre-tax Admins';

const DEFAULT_PREDICTABLE_HEADER = 'Predictable Benefits White-Label';

const DEFAULT_ADMIN_HEADER = 'Your Current "Admin-Only" Model';

function PredictableBenefitsHeaderContent({
    predictableBenefitsHeader,
}: {
    predictableBenefitsHeader: string;
}) {
    if (predictableBenefitsHeader === DEFAULT_PREDICTABLE_HEADER) {
        return (
            <>
                Predictable Benefits <br /> White-Label
            </>
        );
    }
    return <>{predictableBenefitsHeader}</>;
}

function AdminOnlyHeaderContent({
    adminOnlyModelHeader,
}: {
    adminOnlyModelHeader: string;
}) {
    if (adminOnlyModelHeader === DEFAULT_ADMIN_HEADER) {
        return (
            <>
                Your Current <br /> &quot;Admin-Only&quot; Model
            </>
        );
    }
    return <>{adminOnlyModelHeader}</>;
}

const DEFAULT_ROWS: StrategicComparisonRow[] = [
    {
        label: 'Member Journey',
        predictableBenefits: (
            <>
                <span className="text-brand-700 font-medium">Unified</span>{' '}
                (Shop, enroll, and manage in one portal)
            </>
        ),
        adminOnlyModel: 'Fragmented (Shop elsewhere, submit to you)',
    },
    {
        label: 'Premium Payments',
        predictableBenefits: (
            <>
                <span className="text-brand-700 font-medium">Automated</span>{' '}
                 (Clearinghouse-style routing)
            </>
        ),
        adminOnlyModel: 'Manual/Employee-led (Risk of lapse)',
    },
    {
        label: 'Broker Loyalty',
        predictableBenefits: (
            <>
                <span className="text-brand-700 font-medium">High</span>{' '}
                 (You provide the tech they need)
            </>
        ),
        adminOnlyModel: 'Low (Brokers look for tech-first vendors)',
    },
    {
        label: 'Data Integrity',
        predictableBenefits: (
            <>
                <span className="text-brand-700 font-medium">API-driven</span>{' '}
                 (Direct carrier/enrollment feeds)
            </>
        ),
        adminOnlyModel: 'Manual entry (High error rate)',
    },
];

export function StrategicComparison({
    title = DEFAULT_TITLE,
    predictableBenefitsHeader = DEFAULT_PREDICTABLE_HEADER,
    adminOnlyModelHeader = DEFAULT_ADMIN_HEADER,
    rows = DEFAULT_ROWS,
    className,
    tableClassName,
}: StrategicComparisonProps) {
    return (
        <section
            className={clsx(
                'overflow-hidden px-5 py-16 md:px-10 bg-gray-100 md:py-16 lg:px-[79px] lg:py-20',
                className,
            )}
        >
            <div className="mx-auto w-full max-w-[1280px]">
                <Elements.Title
                    title={title}
                    type="h2"
                    className="mb-8 max-w-[600px] mx-auto text-center text-3xl font-medium text-gray-900 md:mb-10 md:text-[32px] lg:text-6xl lg:font-medium"
                />
                <div className="p-2 bg-[#F9FAFB] max-w-[1200px] mx-auto rounded-2xl shadow-[0px_0px_4px_0px_#0000001F_inset]">
                    <div className="flex flex-col gap-6 md:hidden">
                        <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0px_0px_11.9px_2px_#00000017]">
                            <div className="rounded-t-2xl bg-brand-700 px-4 py-4 text-center text-lg font-medium text-gray-50">
                                <PredictableBenefitsHeaderContent
                                    predictableBenefitsHeader={
                                        predictableBenefitsHeader
                                    }
                                />
                            </div>
                            <div className="divide-y divide-gray-200">
                                {rows.map((row) => (
                                    <div
                                        key={`pb-${row.label}`}
                                        className="px-4 py-4"
                                    >
                                        <div className="mb-2 text-sm font-normal uppercase text-gray-450">
                                            {row.label}
                                        </div>
                                        <div className="text-base font-medium leading-snug text-gray-800">
                                            {row.predictableBenefits}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
                            <div className="rounded-t-2xl border-b border-gray-200 bg-white px-4 py-4 text-center text-lg font-medium text-gray-800">
                                <AdminOnlyHeaderContent
                                    adminOnlyModelHeader={adminOnlyModelHeader}
                                />
                            </div>
                            <div className="divide-y divide-gray-200">
                                {rows.map((row) => (
                                    <div
                                        key={`adm-${row.label}`}
                                        className="px-4 py-4"
                                    >
                                        <div className="mb-2 text-sm font-normal uppercase text-gray-450">
                                            {row.label}
                                        </div>
                                        <div className="text-base font-medium leading-snug text-gray-800">
                                            {row.adminOnlyModel}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <table
                        className={clsx(
                            'hidden md:table table-fixed w-full rounded-2xl mx-auto py-4 px-4 border-separate border-spacing-0 bg-white text-left text-gray-900 md:min-w-0',
                            tableClassName,
                        )}
                    >
                        <colgroup>
                            <col className="w-1/3" />
                            <col className="w-1/3" />
                            <col className="w-1/3" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="rounded-tl-2xl border-b border-r border-gray-200 bg-gray-50 px-4 py-4 text-sm font-semibold text-gray-600 md:px-5"
                                >
                                    <span className="sr-only">Criteria</span>
                                </th>
                                <th
                                    scope="col"
                                    className="rounded-t-2xl bg-brand-700 px-4 py-4 text-sm text-center font-medium text-gray-50 md:px-5 md:text-xl lg:text-2xl"
                                >
                                    <PredictableBenefitsHeaderContent
                                        predictableBenefitsHeader={
                                            predictableBenefitsHeader
                                        }
                                    />
                                </th>
                                <th
                                    scope="col"
                                    className="rounded-tr-2xl px-4 py-4 text-sm text-center font-medium text-gray-800 md:px-5 md:text-xl lg:text-2xl"
                                >
                                    <AdminOnlyHeaderContent
                                        adminOnlyModelHeader={
                                            adminOnlyModelHeader
                                        }
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const isLast = index === rows.length - 1;
                                return (
                                    <tr key={row.label}>
                                        <th
                                            scope="row"
                                            className={clsx(
                                                'min-w-0 wrap-break-word border-r border-gray-200 bg-gray-50 px-4 py-4 text-sm font-medium text-gray-800 md:px-5 md:text-xl',
                                                !isLast && 'border-b border-gray-200',
                                                isLast && 'rounded-bl-2xl',
                                            )}
                                        >
                                            {row.label}
                                        </th>
                                        <td
                                            className={clsx(
                                                'min-w-0 wrap-break-word border-r shadow-[0px_0px_11.9px_2px_#00000017] border-gray-200 bg-white px-4 py-4 text-sm leading-snug text-gray-800 md:px-5 md:text-xl',
                                                !isLast && 'border-b border-gray-200',
                                                isLast && 'rounded-b-2xl',
                                            )}
                                        >
                                            {row.predictableBenefits}
                                        </td>
                                        <td
                                            className={clsx(
                                                'min-w-0 wrap-break-word px-4 py-4 text-sm leading-snug text-gray-800 md:px-5 md:text-xl',
                                                !isLast && 'border-b border-gray-200',
                                                isLast && 'rounded-br-2xl',
                                            )}
                                        >
                                            {row.adminOnlyModel}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}
